/**
 * Live preview of the gift card on the product page.
 * Reads the recipient fields and the selected denomination, writes with textContent only.
 */
class GiftCardPreview extends HTMLElement {
  /** @type {Record<string, string>} */
  #amounts = {};
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {AbortController | null} */
  #abort = null;
  #selfMode = false;

  connectedCallback() {
    this.#form = /** @type {HTMLFormElement | null} */ (this.closest('form'));
    if (!this.#form) return;

    try {
      const raw = this.querySelector('script[data-amounts]')?.textContent;
      this.#amounts = raw ? JSON.parse(raw) : {};
    } catch (_e) {
      this.#amounts = {};
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Typing in any recipient field (events bubble up to the form)
    this.#form.addEventListener('input', () => this.#onInput(), { signal });
    this.#form.addEventListener('change', () => this.#onInput(), { signal });

    // The theme dispatches variant:update (ThemeEvents.variantUpdate) when a denomination is picked
    document.addEventListener(
      'variant:update',
      (event) => {
        const detail = /** @type {CustomEvent} */ (event).detail;
        this.#setAmount(detail?.resource?.id);
      },
      { signal }
    );

    // The recipient form announces "It's for me" / "Send it to someone instead"
    this.#form.addEventListener(
      'recipient:toggle',
      (event) => {
        this.#selfMode = !(/** @type {CustomEvent} */ (event).detail?.recipientFormVisible ?? true);
        this.#render();
      },
      { signal }
    );

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #onInput() {
    this.#render();
  }

  /** @param {string | number} [variantId] */
  #setAmount(variantId) {
    this.#render(variantId === undefined ? undefined : String(variantId));
  }

  /** @param {string} [variantId] */
  #render(variantId) {
    const form = this.#form;
    if (!form) return;

    /** @param {string} key */
    const field = (key) => {
      const el = /** @type {HTMLInputElement | HTMLTextAreaElement | null} */ (
        form.querySelector(`[name="properties[${key}]"]`)
      );
      return el && !el.disabled ? el.value.trim() : '';
    };

    this.hidden = this.#selfMode;
    if (this.#selfMode) return;

    const name = field('Recipient name');
    const note = field('Message');
    const from = field('From');

    const forEl = this.querySelector('[data-preview="for"]');
    if (forEl) {
      forEl.textContent = name ? (this.dataset.forTemplate || '').replace('[name]', () => name) : '';
      /** @type {HTMLElement} */ (forEl).hidden = !name;
    }

    const messageEl = this.querySelector('[data-preview="message"]');
    if (messageEl) {
      messageEl.textContent = note || this.dataset.placeholder || '';
      messageEl.classList.toggle('gift-card-preview__message--empty', !note);
    }

    const fromEl = this.querySelector('[data-preview="from"]');
    if (fromEl) {
      fromEl.textContent = from ? (this.dataset.fromTemplate || '').replace('[name]', () => from) : '';
      /** @type {HTMLElement} */ (fromEl).hidden = !from;
    }

    const id = variantId || String(form.querySelector('[name="id"]')?.value ?? '');
    const amountEl = this.querySelector('[data-preview="amount"]');
    if (amountEl && this.#amounts[id]) amountEl.textContent = this.#amounts[id];
  }
}

if (!customElements.get('gift-card-preview')) customElements.define('gift-card-preview', GiftCardPreview);
